/**
 * MainGameScene - メインゲームシーン
 * カード表示、アニメーション、ゲーム状態の同期を担当
 */
import Phaser from 'phaser';
import { PHASER_CONFIG, ANIMATION_CONFIG } from '../../utils/constants';
import { CardSprite } from '../sprites/CardSprite';
import { DeckSprite } from '../sprites/DeckSprite';
import { DiscardPileSprite } from '../sprites/DiscardPileSprite';
import { PositionCalculator, TeamLayoutInfo } from '../utils/PositionCalculator';
import { ZustandBridge } from '../bridge/ZustandBridge';
import { Card } from '../../core/Card';
import { GameState } from '../../types/game';
import { Player } from '../../types/player';
import { useGameStore } from '../../stores/gameStore';
import { useUIStore } from '../../stores/uiStore';
import { CardType } from '../../types/card';
import { SoundManager, setSoundManager } from '@/services/SoundManager';

export class MainGameScene extends Phaser.Scene {
  // スプライト
  private deckSprite!: DeckSprite;
  private discardPileSprite!: DiscardPileSprite;
  private playerCards: Map<string, CardSprite[]> = new Map();

  // チームオーバーレイ（Team 1視覚化）
  private teamOverlay!: Phaser.GameObjects.Container;

  // Zustand連携
  private zustandBridge!: ZustandBridge;

  // サウンドマネージャー
  private soundManager!: SoundManager;

  // 現在のゲーム状態
  private currentGameState: GameState | null = null;
  private localPlayerId: string | null = null;

  // アニメーション中フラグ
  private isAnimating: boolean = false;

  // 現在のローカルプレイヤーのチーム（オーバーレイ更新用）
  private currentLocalPlayerTeam: number = 1;

  constructor() {
    super({ key: 'MainGameScene' });
  }

  create(): void {
    this.createTeamOverlay();  // 最初にオーバーレイを作成（最背面）
    this.createGameObjects();
    this.setupSoundManager();
    this.setupZustandBridge();

    // issue #56: ゲーム開始時に BGM を再生 (bgm_game = The hunter and the hunted)
    // ファイル未配置の場合は SoundManager 側 graceful degradation で無音継続
    this.soundManager.playGameBGM();

    // デバッグ: Phaser入力システムの動作確認
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      console.log('Phaser scene pointer down:', pointer.x, pointer.y);
    });
  }

  /**
   * ローカルプレイヤーのチーム番号を取得
   */
  private getLocalPlayerTeam(): number {
    if (!this.currentGameState || !this.localPlayerId) return 1;
    const localPlayer = this.currentGameState.players.find(
      p => String(p.id) === String(this.localPlayerId)
    );
    return localPlayer?.team ?? 1;
  }

  /**
   * ローカルプレイヤーのインデックスを取得
   */
  private getLocalPlayerIndex(): number {
    if (!this.currentGameState || !this.localPlayerId) return 0;
    return this.currentGameState.players.findIndex(
      p => String(p.id) === String(this.localPlayerId)
    );
  }

  /**
   * チームレイアウト情報を構築
   */
  private buildTeamLayoutInfo(): TeamLayoutInfo | undefined {
    if (!this.currentGameState) return undefined;
    const localPlayerTeam = this.getLocalPlayerTeam();
    const playerTeams = this.currentGameState.players.map(p => p.team);
    return { localPlayerTeam, playerTeams };
  }

  /**
   * Team 1オーバーレイを作成（プロトタイプではReactオーバーレイに置換のため空実装）
   * 後で再有効化する可能性があるため、コンテナのみ生成して保持する。
   */
  private createTeamOverlay(): void {
    this.teamOverlay = this.add.container(0, 0);
    this.teamOverlay.setDepth(-1);
    // プロトタイプ版インゲームUIではReactの PlayerStatusOverlay/TeamInfoBar に統一する
  }

  /**
   * チームオーバーレイを更新（プロトタイプではReactオーバーレイに置換のため空実装）
   */
  private updateTeamOverlay(_localPlayerTeam: number): void {
    // 空処理: React オーバーレイ側で表示するため Phaser 側は何もしない
  }

  /**
   * サウンドマネージャーを設定
   */
  private setupSoundManager(): void {
    this.soundManager = new SoundManager(this);
    setSoundManager(this.soundManager);
    console.log('SoundManager initialized');
  }

  /**
   * ゲームオブジェクトを作成
   */
  private createGameObjects(): void {
    // デッキスプライト
    this.deckSprite = new DeckSprite(
      this,
      PHASER_CONFIG.DECK_POSITION.x,
      PHASER_CONFIG.DECK_POSITION.y
    );
    this.add.existing(this.deckSprite);

    // 捨て札スプライト
    this.discardPileSprite = new DiscardPileSprite(
      this,
      PHASER_CONFIG.DISCARD_POSITION.x,
      PHASER_CONFIG.DISCARD_POSITION.y
    );
    this.add.existing(this.discardPileSprite);

    // デッキクリックイベント
    this.deckSprite.on('pointerdown', () => {
      this.onDeckClick();
    });
  }

  /**
   * Zustand連携を設定
   */
  private setupZustandBridge(): void {
    this.zustandBridge = new ZustandBridge();
    this.zustandBridge.connect(this);
  }

  /**
   * ゲーム状態を同期
   */
  public syncWithGameState(gameState: GameState, localPlayerId: string): void {
    // ターン主体がローカルプレイヤー以外になった瞬間にホバープレビューを強制クリア
    // （ホバー中に AI/相手のターンになった場合、stale なプレビューが残るのを防ぐ）
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (currentPlayer && String(currentPlayer.id) !== String(localPlayerId)) {
      useUIStore.getState().clearCardHoverPreview();
    }

    this.currentGameState = gameState;
    this.localPlayerId = localPlayerId;

    // ローカルプレイヤーのチームが変わったらオーバーレイを更新
    const newLocalPlayerTeam = this.getLocalPlayerTeam();
    if (newLocalPlayerTeam !== this.currentLocalPlayerTeam) {
      this.currentLocalPlayerTeam = newLocalPlayerTeam;
      this.updateTeamOverlay(newLocalPlayerTeam);
    }

    // デッキ枚数更新
    if (gameState.deck) {
      this.deckSprite.updateCount(gameState.deck.length);
    }

    // 捨て札更新
    if (gameState.discardPile && gameState.discardPile.length > 0) {
      const topCard = gameState.discardPile[gameState.discardPile.length - 1];
      this.discardPileSprite.setTopCard(topCard);

      // 宣言された色を表示
      if (gameState.declaredColor) {
        this.discardPileSprite.showDeclaredColor(gameState.declaredColor);
      }
    }

    // プレイヤー手札を更新
    this.updatePlayerHands(gameState.players, localPlayerId);
  }

  /**
   * プレイヤー手札を更新
   */
  private updatePlayerHands(players: Player[], localPlayerId: string): void {
    const localPlayerIndex = this.getLocalPlayerIndex();
    const teamLayout = this.buildTeamLayoutInfo();

    players.forEach((player, index) => {
      const position = PositionCalculator.getPlayerPosition(
        index,
        players.length,
        localPlayerIndex,
        teamLayout
      );
      // 型の一致を確保（numberとstringの比較問題を回避）
      const isLocalPlayer = String(player.id) === String(localPlayerId);

      // 可視性をgameStoreのcanViewHandに統一
      // (deal animation と updatePlayerHands で別々のロジックを持つと、
      //  Solo Team1 でパートナー手札が再表示時に裏返るなどの不整合が起きる)
      const canViewHand = isLocalPlayer
        ? true
        : useGameStore.getState().canViewHand(localPlayerId, player.id);

      const cards = player.hand;

      // 既存のカードスプライトを取得または作成
      let cardSprites = this.playerCards.get(player.id);
      if (!cardSprites) {
        cardSprites = [];
        this.playerCards.set(player.id, cardSprites);
      }

      // カード枚数の差分を処理
      const diff = cards.length - cardSprites.length;

      if (diff > 0) {
        // カードを追加
        // Note: originalLengthを保存しないと、push後にcardSprites.lengthが増加し、
        // インデックスがずれてundefinedになる
        const originalLength = cardSprites.length;
        for (let i = 0; i < diff; i++) {
          const card = cards[originalLength + i];
          if (!card) {
            console.warn(`Card at index ${originalLength + i} is undefined`);
            continue;
          }
          const cardSprite = new CardSprite(this, 0, 0, card, !canViewHand);
          this.add.existing(cardSprite);
          cardSprites.push(cardSprite);
        }
      } else if (diff < 0) {
        // カードを削除
        for (let i = 0; i < Math.abs(diff); i++) {
          const sprite = cardSprites.pop();
          if (sprite) {
            sprite.destroy();
          }
        }
      }

      // カードデータを更新
      cardSprites.forEach((sprite, i) => {
        if (cards[i]) {
          sprite.updateCard(cards[i], !canViewHand);
        }
      });

      // カード位置を計算して適用
      const positions = PositionCalculator.calculateHandPositions(
        cards.length,
        position
      );

      cardSprites.forEach((sprite, i) => {
        if (positions[i]) {
          sprite.setPosition(positions[i].x, positions[i].y);
          sprite.setRotation(positions[i].rotation || 0);
        }
      });

      // ローカルプレイヤーのカードにクリックイベントを設定
      if (isLocalPlayer) {
        console.log(`Setting up click handlers for local player ${player.id}, ${cardSprites.length} cards`);
        cardSprites.forEach((sprite, i) => {
          // CardSpriteのsetupInteractive()で既にヒットエリアが設定されているため、
          // setInteractive()を再呼び出しするとヒットエリアがクリアされる問題を回避
          sprite.off('pointerdown');
          sprite.on('pointerdown', () => {
            console.log(`Pointer down on card index ${i}`);
            // クリック時はホバープレビューを即座に解除
            // （カードが destroy される前に発火させ、stale 状態を防ぐ）
            useUIStore.getState().clearCardHoverPreview();
            this.onCardClick(sprite);
          });

          // ホバープレビュー（次の順番/ペナルティ対象の予告表示）
          sprite.setHoverPreviewCallbacks(
            (card: Card) => {
              const drawAmount =
                card.type === CardType.DRAW_TWO
                  ? 2
                  : card.type === CardType.WILD_DRAW_FOUR
                    ? 4
                    : undefined;
              useUIStore.getState().setCardHoverPreview({
                cardType: card.type,
                drawAmount,
              });
            },
            () => {
              useUIStore.getState().clearCardHoverPreview();
            }
          );
        });
      } else {
        // 非ローカルプレイヤーのカードにはプレビューを発火させない
        cardSprites.forEach((sprite) => {
          sprite.setHoverPreviewCallbacks(null, null);
        });
      }
    });
  }

  /**
   * プレイ可能カードをハイライト
   */
  public highlightPlayableCards(playableCardIds: Set<string>): void {
    this.playerCards.forEach((sprites) => {
      sprites.forEach((sprite) => {
        const isPlayable = playableCardIds.has(sprite.getCardId());
        sprite.setPlayable(isPlayable);
      });
    });
  }

  /**
   * カードクリック処理
   */
  private onCardClick(cardSprite: CardSprite): void {
    console.log('Card clicked:', cardSprite.getCardId());
    if (this.isAnimating) {
      console.log('Animation in progress, ignoring click');
      return;
    }

    const cardId = cardSprite.getCardId();
    this.zustandBridge.onCardClicked(cardId);
  }

  /**
   * デッキクリック処理
   */
  private onDeckClick(): void {
    if (this.isAnimating) return;

    this.zustandBridge.onDeckClicked();
  }

  /**
   * カードプレイアニメーション
   */
  public playCardAnimation(
    cardId: string,
    fromPlayerId: string,
    callback?: () => void
  ): void {
    const sprites = this.playerCards.get(fromPlayerId);
    if (!sprites) {
      callback?.();
      return;
    }

    const cardSprite = sprites.find((s) => s.getCardId() === cardId);
    if (!cardSprite) {
      callback?.();
      return;
    }

    this.isAnimating = true;

    // 手札リストから削除
    const index = sprites.indexOf(cardSprite);
    if (index > -1) {
      sprites.splice(index, 1);
    }

    // 捨て札へのアニメーション
    this.tweens.add({
      targets: cardSprite,
      x: PHASER_CONFIG.DISCARD_POSITION.x,
      y: PHASER_CONFIG.DISCARD_POSITION.y,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: ANIMATION_CONFIG.CARD_PLAY_DURATION,
      ease: 'Back.easeOut',
      onComplete: () => {
        // スケールを戻す
        this.tweens.add({
          targets: cardSprite,
          scaleX: 1,
          scaleY: 1,
          duration: 100,
          onComplete: () => {
            cardSprite.destroy();
            this.isAnimating = false;
            callback?.();
          },
        });
      },
    });
  }

  /**
   * カードドローアニメーション
   */
  public drawCardAnimation(
    toPlayerId: string,
    card: Card,
    callback?: () => void
  ): void {
    const players = this.currentGameState?.players || [];
    const playerIndex = players.findIndex((p) => p.id === toPlayerId);
    if (playerIndex === -1) {
      callback?.();
      return;
    }

    const isLocalPlayer = toPlayerId === this.localPlayerId;
    const localPlayerIndex = this.getLocalPlayerIndex();
    const teamLayout = this.buildTeamLayoutInfo();
    const position = PositionCalculator.getPlayerPosition(
      playerIndex,
      players.length,
      localPlayerIndex,
      teamLayout
    );

    // 新しいカードスプライトを作成（最初は裏面）
    const cardSprite = new CardSprite(
      this,
      PHASER_CONFIG.DECK_POSITION.x,
      PHASER_CONFIG.DECK_POSITION.y,
      card,
      true // 最初は裏面
    );
    this.add.existing(cardSprite);

    // スプライトリストに追加
    let sprites = this.playerCards.get(toPlayerId);
    if (!sprites) {
      sprites = [];
      this.playerCards.set(toPlayerId, sprites);
    }
    sprites.push(cardSprite);

    // 手札位置を計算
    const handPositions = PositionCalculator.calculateHandPositions(
      sprites.length,
      position
    );
    const targetPos = handPositions[handPositions.length - 1];

    this.isAnimating = true;

    // 手札へのアニメーション
    this.tweens.add({
      targets: cardSprite,
      x: targetPos.x,
      y: targetPos.y,
      rotation: targetPos.rotation || 0,
      duration: ANIMATION_CONFIG.CARD_DRAW_DURATION,
      ease: 'Power2',
      onComplete: () => {
        // ローカルプレイヤーならカードを表にする
        if (isLocalPlayer) {
          cardSprite.flipToFront();
          // CardSprite生成時のカスタムヒットエリアを維持するため、再setInteractiveしない
          cardSprite.on('pointerdown', () => {
            this.onCardClick(cardSprite);
          });
        }
        this.isAnimating = false;
        callback?.();
      },
    });
  }

  /**
   * カード配布アニメーション
   */
  public dealCardsAnimation(
    players: Player[],
    localPlayerId: string,
    callback?: () => void
  ): void {
    // 既存のカードをクリア（重複防止）
    this.playerCards.forEach(sprites => {
      sprites.forEach(sprite => sprite.destroy());
    });
    this.playerCards.clear();

    this.isAnimating = true;
    let animationCount = 0;
    const totalCards = players.reduce((sum, p) => sum + p.hand.length, 0);

    // ローカルプレイヤー情報を先に取得
    const localPlayerIndex = players.findIndex(p => p.id === localPlayerId);
    const localPlayer = players.find(p => p.id === localPlayerId);
    const localPlayerTeam = localPlayer?.team ?? 1;
    const playerTeams = players.map(p => p.team);
    const teamLayout: TeamLayoutInfo = { localPlayerTeam, playerTeams };

    players.forEach((player, playerIndex) => {
      const position = PositionCalculator.getPlayerPosition(
        playerIndex,
        players.length,
        localPlayerIndex,
        teamLayout
      );
      const isLocalPlayer = player.id === localPlayerId;

      // 最終手札サイズに基づいて全カードの目標位置を事前計算
      // 配布中に sprites.length をその場で参照すると、
      // 各カードがそれぞれ異なる「現時点の手札サイズ」での位置を取りに行ってしまい、
      // 最終的なカード配置が中央寄りに偏る問題が発生する。
      const finalHandPositions = PositionCalculator.calculateHandPositions(
        player.hand.length,
        position
      );

      player.hand.forEach((card, cardIndex) => {
        const delay = (playerIndex * player.hand.length + cardIndex) * 100;

        this.time.delayedCall(delay, () => {
          const cardSprite = new CardSprite(
            this,
            PHASER_CONFIG.DECK_POSITION.x,
            PHASER_CONFIG.DECK_POSITION.y,
            card,
            true
          );
          this.add.existing(cardSprite);

          let sprites = this.playerCards.get(player.id);
          if (!sprites) {
            sprites = [];
            this.playerCards.set(player.id, sprites);
          }
          sprites.push(cardSprite);

          // 事前計算した最終位置を使用（cardIndex で固定）
          const targetPos = finalHandPositions[cardIndex] ?? finalHandPositions[finalHandPositions.length - 1];

          this.tweens.add({
            targets: cardSprite,
            x: targetPos.x,
            y: targetPos.y,
            rotation: targetPos.rotation || 0,
            duration: ANIMATION_CONFIG.CARD_DEAL_DURATION,
            ease: 'Power2',
            onComplete: () => {
              // 可視性を判定
              const canViewHand = useGameStore.getState().canViewHand(localPlayerId, player.id);
              if (canViewHand) {
                cardSprite.flipToFront();
              }

              // ローカルプレイヤーのみクリック可能にする
              if (isLocalPlayer) {
                // CardSprite生成時のカスタムヒットエリアを維持するため、再setInteractiveしない
                cardSprite.on('pointerdown', () => {
                  this.onCardClick(cardSprite);
                });
              }

              animationCount++;
              if (animationCount >= totalCards) {
                this.isAnimating = false;
                callback?.();
              }
            },
          });
        });
      });
    });
  }

  /**
   * 勝利エフェクト
   */
  public showWinCelebration(_winningTeam: string): void {
    const particles = this.add.particles(
      PHASER_CONFIG.WIDTH / 2,
      PHASER_CONFIG.HEIGHT / 2,
      'particle',
      {
        speed: { min: 200, max: 400 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.6, end: 0 },
        lifespan: 1500,
        tint: [0xe74c3c, 0x2ecc71, 0xf39c12, 0x3498db],
        quantity: 50,
        emitting: false,
      }
    );

    particles.explode(100);

    this.time.delayedCall(2000, () => {
      particles.destroy();
    });
  }

  /**
   * シーン破棄時のクリーンアップ
   */
  shutdown(): void {
    // ホバープレビューが残らないように明示クリア
    useUIStore.getState().clearCardHoverPreview();
    this.zustandBridge?.disconnect();
    this.playerCards.clear();
  }
}
